import React, { MouseEventHandler, ReactElement } from 'react';
// eslint-disable-next-line no-restricted-imports
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  styled,
  Typography,
} from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Close } from '@mui/icons-material';

export type ConfirmationDialogProps = {
  open: boolean;
  onClose?: MouseEventHandler;
  onConfirm?: MouseEventHandler;
  dialogText?: string;
  dialogContent?: ReactElement;
  dialogTitle?: string;
  primaryActionLabel?: string;
  secondaryActionLabel?: string;
  showCloseButton?: boolean;
  autoFocus?: boolean;
  loading?: boolean;
  /** @deprecated Use onClose handler to check reason parameter instead */
  disableBackdropClick?: boolean;
  /** @deprecated MUI v7 removed this prop, use onClose handler instead */
  disableEscapeKeyDown?: boolean;
};

const TitleContainer = styled('div')(({ theme }) => ({
  height: theme.spacing(8),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(0, 1, 0, 0),
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(1, 4, 0, 4),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  height: theme.spacing(8),
  alignItems: 'center',
  padding: theme.spacing(0, 4),
  '& > :not(:first-of-type)': {
    marginLeft: theme.spacing(2),
  },
}));

export const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  dialogText,
  dialogContent,
  dialogTitle = 'ARE YOU SURE?',
  secondaryActionLabel = 'Cancel',
  primaryActionLabel = 'Delete',
  showCloseButton = false,
  autoFocus = true,
  loading = false,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
}: ConfirmationDialogProps): ReactElement => {
  const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    if (disableEscapeKeyDown && reason === 'escapeKeyDown') {
      return;
    }
    onClose?.(event as React.MouseEvent);
  };

  return (
    <Dialog
      open={open}
      fullWidth={true}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      data-testid="alert-dialog"
    >
      <TitleContainer>
        <DialogTitle id="alert-dialog-title" data-testid="alert-title">
          <Typography variant="body1" color="text.secondary">
            {dialogTitle}
          </Typography>
        </DialogTitle>
        {showCloseButton && (
          <IconButton color="secondary" aria-label="close" onClick={onClose}>
            <Close />
          </IconButton>
        )}
      </TitleContainer>
      <Divider />
      {dialogContent ? (
        dialogContent
      ) : (
        <StyledDialogContent>
          <DialogContentText id="alert-dialog-description">{dialogText}</DialogContentText>
        </StyledDialogContent>
      )}
      {!loading ? <Divider /> : <LinearProgress />}
      <StyledDialogActions>
        <Button
          variant="outlined"
          onClick={onClose}
          color="secondary"
          data-testid="cancel-action"
          aria-label="cancel-delete"
        >
          {secondaryActionLabel}
        </Button>
        <Button
          onClick={onConfirm}
          color="primary"
          autoFocus={autoFocus}
          data-testid="confirm-action"
          aria-label="confirm-delete"
        >
          {primaryActionLabel}
        </Button>
      </StyledDialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
